import { css, Global } from '@emotion/react';
import { Box, IconButton, Popover } from '@lego/klik-ui';
import { RobotSolid } from '@lego/klik-ui/icons';
import { type UIMessage, useChat } from '@tanstack/ai-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccessToken } from '@/hooks/use_access_token';
import { usePageContext } from '@/providers/PageContextProvider';
import { resolveAction } from './approvals';
import { ChatPanel } from './ChatPanel';
import { createChatConnection, type PendingAction } from './connection';

/**
 * Maximized chat fills the viewport. Detaches the Chakra popper wrapper from the
 * trigger anchor. !important is required because Chakra/popper.js set these
 * inline, which outrank selector specificity. Scoped to the maximized state via
 * the marker class.
 */
const maximizedStyles = (
  <Global
    styles={css`
      .chakra-popover__popper:has(.aui-content-maximized) {
        position: fixed !important;
        inset: 0 !important;
        min-width: 100vw !important;
        transform: none !important;
        z-index: 1500 !important;
      }
      .aui-content-maximized {
        width: 100vw;
        height: 100dvh;
      }
    `}
  />
);

const STORAGE_KEY = 'emma-assistant-chat';
const THREAD_STORAGE_KEY = 'emma-assistant-thread-id';

const loadThreadId = (): string => {
  try {
    return sessionStorage.getItem(THREAD_STORAGE_KEY) || crypto.randomUUID();
  } catch {
    return crypto.randomUUID();
  }
};

const loadMessages = (): UIMessage[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as UIMessage[]).map((m) => ({
      ...m,
      createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
    }));
  } catch {
    return [];
  }
};

export const ChatWidget = () => {
  const [conversation, setConversation] = useState(() => ({
    threadId: loadThreadId(),
    initialMessages: loadMessages(),
  }));
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const getAccessToken = useAccessToken();
  const pageContext = usePageContext();

  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;

  const connection = useMemo(
    () =>
      createChatConnection({
        getAccessToken,
        getContext: () => pageContextRef.current,
        onPendingActions: (incoming) =>
          setPendingActions((prev) => {
            const seen = new Set(prev.map((a) => a.actionId));
            return [...prev, ...incoming.filter((a) => !seen.has(a.actionId))];
          }),
      }),
    [getAccessToken]
  );

  const { messages, sendMessage, isLoading, clear } = useChat({
    connection,
    ...conversation,
  });

  const handleResolve = useCallback(
    async (action: PendingAction, decision: 'approve' | 'reject') => {
      setResolvingId(action.actionId);
      try {
        // Re-fetch the token at click time: the approval prompt can sit open long enough for the
        // turn's original token to expire. getToken() serves a cached token, refreshing near expiry.
        const token = await getAccessToken();
        const status = await resolveAction(
          import.meta.env.VITE_MCP_URL,
          token,
          action.actionId,
          decision
        );
        const verb = decision === 'approve' ? 'approved' : 'rejected';
        setPendingActions((prev) => prev.filter((a) => a.actionId !== action.actionId));
        // Feed the outcome back as the next turn so the assistant can confirm and continue.
        void sendMessage(`Action "${action.tool}" was ${verb} by the user (status: ${status}).`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Unknown error';
        // Keep the action in the list so the user can retry — the POST may have failed before the
        // server resolved it, leaving it still pending server-side.
        void sendMessage(`Action "${action.tool}" failed: ${msg}`);
      } finally {
        setResolvingId(null);
      }
    },
    [getAccessToken, sendMessage]
  );

  useEffect(() => {
    if (isLoading) return;
    try {
      sessionStorage.setItem(THREAD_STORAGE_KEY, conversation.threadId);
      if (messages.length > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore quota / serialization errors — persistence is best-effort.
    }
  }, [messages, isLoading, conversation.threadId]);

  const handleClear = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Persistence is best-effort; clearing the in-memory chat must still work.
    }
    setPendingActions([]);
    clear();
    setConversation({ threadId: crypto.randomUUID(), initialMessages: [] });
  }, [clear]);

  return (
    <>
      {maximizedStyles}
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => {
          setIsOpen(true);
          setIsMaximized(false);
        }}
        placement="top-end"
      >
        <Popover.Trigger>
          <Box bottom={4} position="fixed" right={4} zIndex={1400}>
            <IconButton
              _active={{ transform: 'scale(0.95)' }}
              _hover={{ transform: 'scale(1.1)' }}
              aria-label="Open assistant"
              background="light-blue.600"
              borderRadius="full"
              boxShadow="lg"
              color="white"
              icon={<RobotSolid boxSize="24px" />}
              size="md"
              transition="all 0.2s"
            />
          </Box>
        </Popover.Trigger>

        <Popover.Content
          _focusVisible={{ outline: 'none' }}
          background="transparent"
          border="none"
          borderRadius={isMaximized ? 'none' : 'xl'}
          boxShadow={isMaximized ? 'none' : 'xl'}
          className={isMaximized ? 'aui-content-maximized' : undefined}
          overflow="hidden"
          padding={0}
          width="auto"
          zIndex={1800}
        >
          <ChatPanel
            isLoading={isLoading}
            isMaximized={isMaximized}
            messages={messages}
            onClear={handleClear}
            onClose={() => setIsOpen(false)}
            onResolveAction={handleResolve}
            onSend={sendMessage}
            onToggleMaximize={() => setIsMaximized((v) => !v)}
            pendingActions={pendingActions}
            resolvingId={resolvingId}
          />
        </Popover.Content>
      </Popover>
    </>
  );
};
